import React, {Component} from 'react';
import {withRouter} from 'react-router-dom';
import moment from 'moment';
import ViewRenderer from './views';
import PopupContainer from '../../../components/PopupContainer';
import SimplePopup from '../../../components/SimplePopup';
import Spinner from '../../../components/Spinner';
import {API} from '../../../requests';
import strings from '../../../globalization';
import util from '../../../util';
import {commonImages} from '../../../assets/images';

const POPUP_CONSTANTS = {
  SIMPLE_POPUP: 'simple-popup',
  SPINNER_POPUP: 'spinner-popup',
};

const PAGE_SIZE = 100;

class GeneratedReportsDept extends Component {
  state = {
    totalCount: 0,
    gridApi: null,
    popup: undefined,
  };

  getGeneratedReportsRowsAndTotal = data => {
    if (!data) {
      return {rows: [], total: 0};
    }
    if (Array.isArray(data)) {
      return {rows: data.filter(Boolean), total: data.length};
    }
    const rows = (
      data.content ||
      data.records ||
      data.downloads ||
      data.departmentDownloads ||
      data.reportDownloads ||
      []
    ).filter(Boolean);
    const total =
      data.totalCount ?? data.totalElements ?? data.count ?? 0;
    return {rows, total};
  };

  getData = async (paramsAPI, page) => {
    API.fetchDepartmentGeneratedReports({
      pageNumber: page,
      pageSize: PAGE_SIZE,
    })
      .then(res => {
        const {rows, total} = this.getGeneratedReportsRowsAndTotal(res.data);
        if (paramsAPI) {
          paramsAPI.successCallback(rows, total);
        }
        this.setState({totalCount: total});
      })
      .catch(err => {
        if (paramsAPI) {
          paramsAPI.successCallback([], 0);
        }
        const error = util.getErrorMessage(err);
        this.showErrorPopup(error);
      });
  };

  getDownloadFileName = (downloadUrl, row) => {
    const defaultFileName = `generated-report-${moment().format(
      'DD-MM-YYYY',
    )}.xlsx`;
    const rowFileName = row?.fileName ?? row?.reportName;
    if (rowFileName) {
      return rowFileName.endsWith('.xlsx')
        ? rowFileName
        : `${rowFileName}.xlsx`;
    }
    const urlFileName = downloadUrl.split('?')[0].split('/').pop();
    return urlFileName?.endsWith('.xlsx') ? urlFileName : defaultFileName;
  };

  downloadFileFromUrl = async (downloadUrl, row) => {
    if (!downloadUrl) {
      throw new Error('Download link is not available for this report.');
    }

    const fileName = this.getDownloadFileName(downloadUrl, row);
    const xlsxMimeType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

    const response = await fetch(downloadUrl);
    if (!response.ok) {
      throw new Error('Failed to download report file.');
    }

    const blob = await response.blob();
    const fileBlob =
      blob.type && blob.type !== 'application/octet-stream'
        ? blob
        : new Blob([blob], {type: xlsxMimeType});
    const objectUrl = window.URL.createObjectURL(fileBlob);

    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    window.setTimeout(() => {
      window.URL.revokeObjectURL(objectUrl);
    }, 1000);
  };

  isReportDownloadable = row => {
    if (row?.status !== 'COMPLETED' || !row?.downloadUrl) {
      return false;
    }
    if (row?.expiresAt && moment().isAfter(moment(Number(row.expiresAt)))) {
      return false;
    }
    return true;
  };

  handleDownloadReport = row => {
    if (!this.isReportDownloadable(row)) {
      this.showErrorPopup(
        row?.errorMessage ||
          'This report is not available for download.',
      );
      return;
    }

    this.showSpinner();
    this.downloadFileFromUrl(row.downloadUrl, row)
      .then(() => {
        this.setState({popup: undefined});
      })
      .catch(err => {
        this.setState({popup: undefined});
        this.showErrorPopup(
          util.getErrorMessage(err) ||
            'Failed to download report. Please try again.',
        );
      });
  };

  showSpinner = () => {
    this.setState({
      popup: {
        type: POPUP_CONSTANTS.SPINNER_POPUP,
      },
    });
  };

  showErrorPopup = errorMessage => {
    this.setState({
      popup: {
        type: POPUP_CONSTANTS.SIMPLE_POPUP,
        message: errorMessage,
        messageStyle: {color: '#E67717'},
        headingImage: commonImages.errorIcon,
        onClose: this.closePopup,
        buttons: [
          {
            text: strings.okayPopup,
            onClick: this.closePopup,
            outline: true,
          },
        ],
      },
    });
  };

  closePopup = () => {
    this.setState({
      popup: undefined,
    });
  };

  onChangeHandler = (action, data) => {
    switch (action) {
      case 'gridAPI': {
        this.setState({gridApi: data});
        this.onChangeHandler('updateTable');
        break;
      }
      case 'updateTable': {
        const {gridApi} = this.state;
        if (gridApi) {
          const dataSource = {
            getRows: params => {
              const page = params.endRow / PAGE_SIZE;
              this.getData(params, page - 1);
            },
          };
          gridApi.setDatasource(dataSource);
        }
        break;
      }
      default:
        break;
    }
  };

  onClickHandler = (action, data) => {
    const {history} = this.props;
    switch (action) {
      case 'statusRoute': {
        history.push({
          pathname: '/u/' + data.route,
        });
        break;
      }
      case 'downloadReport': {
        this.handleDownloadReport(data);
        break;
      }
      case 'refreshReports': {
        this.onChangeHandler('updateTable');
        break;
      }
      default:
        break;
    }
  };

  getProps = () => {
    const {totalCount} = this.state;
    return {
      onClick: this.onClickHandler,
      onChange: this.onChangeHandler,
      totalCount,
      pageSize: PAGE_SIZE,
    };
  };

  getPopupContent = () => {
    const {popup} = this.state;
    if (!popup) {
      return null;
    }
    switch (popup.type) {
      case POPUP_CONSTANTS.SIMPLE_POPUP:
        return <SimplePopup {...popup} />;
      case POPUP_CONSTANTS.SPINNER_POPUP:
        return <Spinner name="cube-grid" color="#0045E6" {...popup} />;
      default:
        return null;
    }
  };

  render() {
    const {popup} = this.state;
    return (
      <>
        {popup ? (
          <PopupContainer>{this.getPopupContent()}</PopupContainer>
        ) : null}
        <ViewRenderer {...this.getProps()} />
      </>
    );
  }
}

export default withRouter(GeneratedReportsDept);
